
// --- Vue & Template imports ---
import { Component, Vue} from 'vue-property-decorator';
import DownloadExcel from 'vue-json-excel/JsonExcel.vue';

// --- Models ---
import { DriverPayment } from '@/models/DriverPayment.model';
import { FilterOption } from '../models/App.model';
import { LoadEvent, LoadStop } from '../models/Load.model';

// --- Services ---
import AppService from '@/services/app';
import DriverPaymentService from '@/services/driverPayment';
import DriverService from '@/services/driver';
import LoadEventService from '@/services/loadEvent';
import LoadService from '@/services/load';

// --- Third Party ---
import dayjs from 'dayjs';

@Component({
  components: {   
    DownloadExcel,
  },
})
export default class SearchDriverPayments extends Vue {

  // used by Filter Area
  filterOptions: FilterOption[] = [
    {name: 'Driver', icon: this.$store.getters['app/icons'].searchDriver },
    {name: 'Load Number', icon: this.$store.getters['app/icons'].searchLoadNumber}, 
    {name: 'Date', icon: this.$store.getters['app/icons'].searchDate },
  ];

  searchTypeFilter: FilterOption = { name: '', icon: ''};

  searchDriverId: number = 0;
  searchValue: string = '';
  startDatePicker: boolean = false;
  startDate: string = '';
  endDatePicker: boolean = false;
  endDate: string = '';

  searchClicked: boolean = false;

  searchResults: DriverPayment[] = [];

  expandDelay: boolean = false;
  loadStops: LoadStop[] = [];

  clickedItem: DriverPayment = {
    id: 0,
    loadId: 0,
    loadDate: '',
    horseVehicleId: 0,
    horseVehicleCode: '',
    trailer1VehicleId: 0,
    trailer1VehicleCode: '',
    driverTypeId: 0,
    driverType: '',
    driverId: 0,
    driverNickname: '',
    from: '',
    to: '',
    rate: 0,
    distance: 0,
    totalM3: 0,
    value: 0,
    linkBonus: false,
    isPrimary: true,
    criticalComment: '',
  };

  rules = {
    required: (value: string) => !!value || '',
    numbersOnly: (value: string) => (/^\d+$/.test(value)) || 'Invalid character',
  };

  // used by Load Event Dialog
  showLoadEventDialog: boolean = false;
  loadEventDialogTitle: string = '';
  loadEvents: LoadEvent[] = [];
  commentFormValid: boolean = false;
  newComment: string = '';
  addCommentClicked: boolean = false;

  // #region Functions to display list data on page load
    headers() {
      return [
        {
          text: 'Load#',
          value: 'loadId',
        },
        {
          text: 'Date',
          value: 'loadDate',
        },
        {
          text: 'Type',
          value: 'driverType',
        },
        {
          text: 'Driver',
          value: 'driverNickname',
        },
        {
          text: 'From',
          value: 'from',
        },
        {
          text: 'To',
          value: 'to',
        },
        {
          text: 'Value',
          value: 'value',
        },
        {
          text: 'Bonus',
          value: 'linkBonus',
        },
        {
          text: 'Approved',
          value: 'approved',
        },
        {
          text: 'Finalised',
          value: 'finalised',
        },
        {
            sortable: false,
            text: '',
            value: 'action',
            align: 'end',
          },
      ];
    }

    offloadHeaders() {
      return [
        {
          text: 'Client',
          value: 'client',
        },
        {
          text: 'Address',
          value: 'address',
        },
        {
          text: 'Appointment Time',
          value: 'appointmentTime',
        },
        {
          text: 'Arrived',
          value: 'arrivedAt',
        },
      ];
    }

    async beforeMount() {
      await AppService.listStatuses(false);
      await DriverService.listActive(false);
    }

    getToday() {
      return dayjs().format('YYYY-MM-DD');
    }

    canSearch() {
      switch (this.searchTypeFilter.name) {
        case 'Driver':
          return this.searchDriverId > 0;

        case 'Load Number':
          return this.searchValue;

        case 'Date':
          return this.startDate || this.endDate;
      }
    }

    resetSearch() {
      this.searchResults = [];

      this.searchDriverId = 0;
      this.searchValue = '';
      this.startDatePicker = false;
      this.startDate = dayjs().startOf('month').format('YYYY-MM-DD');
      this.endDatePicker = false;
      this.endDate = this.getToday();

      if (this.searchTypeFilter.name === 'Load Number') {
        this.startDate = '';
        this.endDate = '';
      }

      if (this.$refs.searchForm) {
        (this.$refs.searchForm as any).resetValidation();
      }
    }

    async search() {
      try {
        this.searchClicked = true;

        const response = await DriverPaymentService.search({ params: {
          driverId: this.searchDriverId,
          loadNumber: this.searchValue,
          startDate: this.startDate,
          endDate: this.endDate,
        }});

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
            this.searchResults = [];
          } else {
            this.searchResults = response.data;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Search_DriverPayments.vue - search - 939 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.searchClicked = false;
      }
    }

    async listOffloads(item: DriverPayment) {
      try {
        const response = await LoadService.listOffloadsByLoad({ params: { loadId: item.loadId }});

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
            this.loadStops = [];
          } else {
            this.loadStops = response.data;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Search_Loads.vue - listOffloads - 969 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      }
    }

    async expandPayment(item: DriverPayment, expand: any) {
      this.loadStops = [];

      if (item.offloadCount && item.offloadCount > 0) {
        // go fetch the load stops for this load
        try {
          this.expandDelay = true;
          await this.listOffloads(item);
        } finally {
          this.expandDelay = false;
        }
      }

      expand(true);
    }

    closeRow(expand: any) {
      expand(false);
    }

    allowAddCommentFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Administrator', level: 2},
        { type: 'Operator', level: 3},
      ]);
    }
    
    allowExportFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 1},
        { type: 'Administrator', level: 2},
        { type: 'Operator', level: 3},
      ]);
    }
  // #endregion
  
  // #region Functions required for the CSV Download
  getDataForCSV() {
  return this.searchResults.reduce((a: any, b: any) => {
      let newItem = b;
      newItem = Object.assign({}, b, { linkBonus: b.linkBonus ? 'R100' : 'R0', isPrimary: b.isPrimary ? '' : '[+]' });

      return a.concat(newItem);
    }, []);
  }
  
  getFieldsForCSV() {
    return {
      'Load No': 'loadId',
      'Additional': 'isPrimary',
      'Date': 'loadDate',
      'Type': 'driverType',
      'Driver': 'driverNickname',
      'Truck': 'horseVehicleCode',
      'Trailer 1': 'trailer1VehicleCode',
      'Trailer 2': 'trailer2VehicleCode',
      'From': 'from',
      'To': 'to',
      'Distance': 'distance',
      'Rate': 'rate',
      'Total M3': 'totalM3',        
      'Value': 'value',
      'Link Bonus': 'linkBonus',
      'Approved At': 'approvedAt',
      'Approved By': 'approvedBy',
      'Finalised At': 'finalisedAt',
      'Finalised By': 'finalisedBy',
    };
  }
  // #endregion

  // #region Functions required for the List Load Events / Add Comment Dialog
    async listLoadEvents() {
      try {
        // always check for all the comments in case someone else added one in the background - until we are using websockets
        const response = await LoadEventService.listByLoad({params: {loadId: this.clickedItem.loadId}});

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            this.loadEvents = [];
          } else {
           this.loadEvents = response.data;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Search_DriverPayments.vue - listLoadEvents - 1060 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      }
    }

    async launchLoadEventDialog(item: DriverPayment) {
      this.clickedItem = Object.assign({}, item);
      this.newComment = '';
      
      // fetch load events associated with this load
      await this.listLoadEvents();

      this.commentFormValid = false;
      this.loadEventDialogTitle = 'Events for Load #' + this.clickedItem.loadId;

      if (this.$refs.commentForm) {
        (this.$refs.commentForm as any).resetValidation();
      }

      this.showLoadEventDialog = true;
    }

    async createComment() {
      try {
        this.addCommentClicked = true;

        const response = await LoadEventService.createComment({
          loadId: this.clickedItem.id,
          comment: this.newComment,
          isCritical: false,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            this.showLoadEventDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Search_DriverPayments.vue - createComment - 1110 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.addCommentClicked = false;
      }
    }
  // #endregion
}
